import React, { useEffect, useMemo, useState } from "react";
import casePaidTaskStyle from "./casePaidTaskStyle";
import { Box, Button, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { getInitialValuesForPaidTask, getSubmissionCardEnabled, getTemplates } from "../../services/paidTaskService";
import _ from "lodash";
import CasePaidTaskPdfGenerator from "./CasePaidTaskPdfGenerator";
import FormElementAnnotated from "../Case/CaseForm/FormElementAnnotated";
import CasePaidTaskFileUploadArea from "./CasePaidTaskFileUploadArea";
import { ContentContainer } from "../Container/PageContentContainer";
import ButtonLoading from "../Button/ButtonLoading";
import { getEditorHtml } from "../../services/Editor/editorFunctions";
import { buildAddress } from "../Case/CasePdfGenerator/casePdfGeneratorService";
import { clearSessionStorageState } from "../../services/browserStorageService";
import { PAID_TASK_STATUS_ASSIGNED, PAID_TASK_STATUS_IN_REVIEW } from "../PaidTask/paidTaskStatus";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import { useSnackbar } from "notistack";
import ContentBox from "../ContentBox/ContentBox";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, updateResource } from "../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { FormContainer, RadioButtonGroup, useForm } from "react-hook-form-mui";
import { PaidTask } from "../../types/PaidTask";
import { flatten } from "flat";
import { useCurrentEditor } from "@tiptap/react";
import { ProductUnion } from "../../types/ProductUnion";
import dayjs from "dayjs";

type CasePaidTaskSubmissionProps = {
  paidTask: PaidTask;
  product: ProductUnion;
};

const CasePaidTaskSubmission = ({ paidTask, product }: CasePaidTaskSubmissionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorModalData, setErrorModalData] = useState("");
  const [choiceConfirmed, setChoiceConfirmed] = useState(false);
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { editor } = useCurrentEditor();

  const paidTaskTypeConfig = useMemo(
    () => _.find(getTemplates(product), (template) => template.type === paidTask.type),
    [paidTask.type]
  );

  const initialValues = useMemo(
    () => (paidTaskTypeConfig ? getInitialValuesForPaidTask(paidTaskTypeConfig, product, paidTask) : {}),
    [paidTaskTypeConfig, product, paidTask]
  );

  useEffect(() => {
    if (paidTaskTypeConfig?.hasEditor) {
      editor?.commands.setContent(paidTask.formData?.content);
    }
  }, [paidTaskTypeConfig]);

  const formContext = useForm({
    mode: "onBlur",
    defaultValues: initialValues,
  });

  if (!paidTaskTypeConfig) {
    return (
      <Stack sx={{
        justifyContent: "center"
      }}>Für diesen Ausschreibentyp ist keine Konfiguration hinterlegt</Stack>
    );
  }

  if (paidTaskTypeConfig.hasEditor && !editor) {
    return null;
  }

  const checkNonValidatorConditions = () => {
    if (paidTaskTypeConfig.hasEditor && (!editor || editor.storage.characterCount.characters() < 200)) {
      setErrorModalData("editorContentLength");
      return false;
    }

    if (
      paidTaskTypeConfig.mainDocumentConfig &&
      !_.find(paidTask.mediaObjects, (mediaObject) => mediaObject.identifier === "paidTaskMainDocument")
    ) {
      setErrorModalData("requiredFileMissing");
      return false;
    }
    return true;
  };

  //triggers status change when externals execute it
  const onSubmit = async () => {
    if (!checkNonValidatorConditions()) {
      return;
    }

    const values = formContext.getValues();

    //todo improve when theres enough time. this shouldn't even need to flatten anything. the other code should be able to handle nested objects. (temporary fields)
    const objectFilter: (
      obj: Record<string, any>,
      predicate: (entry: [string, any]) => boolean
    ) => Record<string, any> = (obj, predicate) => Object.fromEntries(Object.entries(obj).filter(predicate));

    const temporaryFields: Record<string, any> = objectFilter(values, ([key]) => key.startsWith("temporary"));
    const normalFields: Record<string, any> = objectFilter(values, ([key]) => !key.startsWith("temporary"));

    const convertedValues: Record<string, any> = {
      ...normalFields,
      ...flatten(temporaryFields, { maxDepth: 2 }),
    };

    // end todo

    setIsLoading(true);
    const formData = _.merge({}, convertedValues);
    if (editor && !editor.isEmpty) {
      formData.content = getEditorHtml(editor);
      formData.address = buildAddress(convertedValues, product);
      clearSessionStorageState("customPdfHtml" + product.id);
    }
    clearSessionStorageState("customPdfValues" + product.id);

    let payload: {
      formData: Record<string, any>;
      markedAsDoneDate?: string;
      changesRequested?: boolean;
    } = {
      formData: formData,
    };

    if (currentUser.isExternal) {
      payload.markedAsDoneDate = dayjs().format("DD.MM.YYYY");
      payload.changesRequested = false;
    }

    await updateMutation.mutateAsync({
      id: paidTask.id,
      uri: "paid_tasks",
      data: payload,
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.item("paid_tasks", paidTask.id)
    });

    if (currentUser.isExternal) {
      enqueueSnackbar("Sie haben diese Aufgabe erledigt", {
        variant: "custom",
        buttonType: "link",
        buttonLink: "/services/extern/aufgaben",
        buttonTarget: "_self",
        buttonText: "Meine Aufgaben",

        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        persist: true,
      });
    }
    setIsLoading(false);
  };

  const formFieldsEnabled =
    currentUser.isExternal || (paidTaskTypeConfig.hasEditor && paidTask.status === PAID_TASK_STATUS_IN_REVIEW);

  const isDisabled = !getSubmissionCardEnabled(paidTask.status, currentUser.isExternal);

  return (
    <Box
      sx={
        isDisabled
          ? {
              opacity: 0.5,
              pointerEvents: "none",
            }
          : {}
      }
    >
      <ContentBox headline="Bearbeitung" headlineVariant="h2">
        <Box sx={casePaidTaskStyle.verticalSpace} />
        <FormContainer formContext={formContext}>
          {paidTaskTypeConfig.hasEditor && (
            <CasePaidTaskPdfGenerator paidTask={paidTask} product={product} initialValues={initialValues} />
          )}
          <ContentContainer size={"tiny"} sx={casePaidTaskStyle.formElementsContainer}>
            <CasePaidTaskFileUploadArea
              paidTaskTypeConfig={paidTaskTypeConfig}
              product={product}
              paidTask={paidTask}
              readOnly={paidTask.status !== PAID_TASK_STATUS_ASSIGNED}
            />
            <Box sx={casePaidTaskStyle.verticalSpace} />
            {_.map(paidTaskTypeConfig.fields, (formElementProps) => (
              <Box sx={casePaidTaskStyle.formElement} key={formElementProps.path}>
                <FormElementAnnotated
                  type={formElementProps.type}
                  headline={formElementProps.headline}
                  description={formElementProps.description}
                  product={product}
                  path={"temporaryField." + formElementProps.path}
                  translationPath={product.productClassName + "." + formElementProps.path}
                  isDisabled={() => !formFieldsEnabled}
                />
              </Box>
            ))}
            <Stack
              direction={"column"}
              spacing={2}
              sx={[{
                alignItems: "center"
              }, casePaidTaskStyle.buttonArea]}>
              {currentUser.isExternal && (
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Checkbox checked={choiceConfirmed} onChange={({ target }) => setChoiceConfirmed(target.checked)} />
                  }
                  label={"Ich möchten diese Aufgabe als erledigt markieren"}
                />
              )}
              {currentUser.isExternal && (
                <ButtonLoading
                  variant={"contained"}
                  isLoading={isLoading}
                  sx={casePaidTaskStyle.submitButton}
                  disabled={!choiceConfirmed}
                  onClick={formContext.handleSubmit(onSubmit)}
                  fullWidth={false}
                >
                  Aufgabe erledigt
                </ButtonLoading>
              )}
              {!currentUser.isExternal &&
                paidTask.status === PAID_TASK_STATUS_IN_REVIEW &&
                paidTaskTypeConfig.hasEditor && (
                  <>
                    <Typography variant={"h3"}>Auswahl der Unterschrift</Typography>
                    <RadioButtonGroup
                      row
                      options={[
                        {
                          id: "internal",
                          label: paidTask.backofficeCase?.lawyer?.person?.fullname || "Interner Anwalt",
                        },
                        { id: "external", label: paidTask.assignee?.person?.fullname || "Externer Anwalt" },
                      ]}
                      name={"sigantureType"}
                    />
                    <ButtonLoading
                      variant={"contained"}
                      isLoading={isLoading}
                      sx={casePaidTaskStyle.submitButton}
                      onClick={formContext.handleSubmit(onSubmit)}
                      fullWidth={false}
                    >
                      Änderungen speichern
                    </ButtonLoading>
                  </>
                )}
            </Stack>
          </ContentContainer>
        </FormContainer>
        <LegalbirdIoModal
          handleClose={() => setErrorModalData("")}
          open={!!errorModalData}
          title={"Erledigung nicht möglich"}
          hideCancelButton
          submitButton={
            <Button onClick={() => setErrorModalData("")} variant={"contained"} color={"primary"}>
              Ok
            </Button>
          }
        >
          {errorModalData === "editorContentLength" &&
            "Der Text im Editor für das Schreiben ist leider mit weniger als 200 Zeichen zu kurz. Ist das Schreiben tatsächlich so kurz, oder haben Sie vergessen, den Text einzufügen?"}
          {errorModalData === "requiredFileMissing" &&
            "Leider haben Sie nicht alle geforderten Dokumente hochgeladen. Bitte prüfen Sie die hochgeladenen Dokumente."}
        </LegalbirdIoModal>
      </ContentBox>
    </Box>
  );
};

export default CasePaidTaskSubmission;
